/**
 * The actions of the current selection, for both right-click menus (the
 * canvas's icon bar and the layers panel's list): a group's, or the shapes'.
 * One list, so the two menus always offer the same.
 */

import { ArrowsPointingOutIcon, ArrowsRightLeftIcon, ArrowsUpDownIcon, DocumentDuplicateIcon, Squares2X2Icon, TrashIcon } from '@heroicons/react/24/outline';
import { useT } from '../../translation/src';
import type { HeroIcon } from '../../types';
import { deleteSelection, duplicateSelection, groupSelection, mirrorSelection, ungroupSelection } from './document/actions';
import { useEditorState, useObjectEditor } from './EditorContext';

export interface SelectionAction {
  id: string;
  icon: HeroIcon;
  /** Menu text / tooltip. */
  label: string;
  /** Keyboard shortcut, when it has one. */
  shortcut?: string;
  run: () => void;
  danger?: boolean;
}

export function useSelectionActions(): SelectionAction[] {
  const { store } = useObjectEditor();
  const { spec, selection } = useEditorState();
  const labels = {
    duplicate: useT('duplicate', { swc: true }),
    flipU: useT('flip left ↔ right', { swc: true }),
    flipN: useT('flip back ↔ front', { swc: true }),
    group: useT('group', { swc: true }),
    ungroup: useT('ungroup', { swc: true }),
    delete: useT('delete', { swc: true }),
  };
  if (selection.kind === 'group')
    return [
      { id: 'ungroup', icon: ArrowsPointingOutIcon, label: labels.ungroup, shortcut: 'Ctrl+Shift+G', run: () => ungroupSelection(store) },
      { id: 'delete', icon: TrashIcon, label: labels.delete, shortcut: 'Del', run: () => deleteSelection(store), danger: true },
    ];
  if (selection.kind === 'shapes')
    return [
      { id: 'duplicate', icon: DocumentDuplicateIcon, label: labels.duplicate, shortcut: 'Ctrl+D', run: () => duplicateSelection(store) },
      { id: 'flipU', icon: ArrowsRightLeftIcon, label: labels.flipU, run: () => mirrorSelection(store, 'u') },
      { id: 'flipN', icon: ArrowsUpDownIcon, label: labels.flipN, run: () => mirrorSelection(store, 'n') },
      { id: 'group', icon: Squares2X2Icon, label: labels.group, shortcut: 'Ctrl+G', run: () => groupSelection(store, `${labels.group} ${spec.groups.length + 1}`) },
      { id: 'delete', icon: TrashIcon, label: labels.delete, shortcut: 'Del', run: () => deleteSelection(store), danger: true },
    ];
  return [];
}
